import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ExpenseActions } from './expense.actions';
import { catchError, map, mergeMap, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ExpenseService } from '@core/services/expense.service';

@Injectable()
export class ExpenseEffects {
  private actions$ = inject(Actions);
  private expenseService = inject(ExpenseService);
  private translateService = inject(TranslateService);

  getExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExpenseActions.loadExpenses),
      mergeMap((data) =>
        this.expenseService.getExpenses(data).pipe(
          map((expense) =>
            expense.success
              ? ExpenseActions.loadExpensesSuccess(expense)
              : ExpenseActions.loadExpensesFailure(expense)
          ),
          catchError((error) =>
            of(
              ExpenseActions.loadExpensesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getExpenseTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExpenseActions.loadExpensesTypes),
      mergeMap(() =>
        this.expenseService.getExpenseTypes().pipe(
          map((expense) =>
            expense.success
              ? ExpenseActions.loadExpensesTypesSuccess(expense)
              : ExpenseActions.loadExpensesTypesFailure(expense)
          ),
          catchError((error) =>
            of(
              ExpenseActions.loadExpensesTypesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  approveTimesheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExpenseActions.approveExpenses),
      mergeMap((data) =>
        this.expenseService.approveExpense(data).pipe(
          map((response) =>
            response.success
              ? ExpenseActions.approveExpensesSuccess(response)
              : ExpenseActions.approveExpensesFailure(response)
          ),
          catchError((error) =>
            of(
              ExpenseActions.approveExpensesFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  editExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExpenseActions.editExpense),
      mergeMap((data) =>
        this.expenseService.editExpense(data.ExpenseDetails).pipe(
          map((response) =>
            response.success
              ? ExpenseActions.editExpenseSuccess(response)
              : ExpenseActions.editExpenseFailure(response)
          ),
          catchError((error) =>
            of(
              ExpenseActions.editExpenseFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  addComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExpenseActions.addComment),
      mergeMap((data) =>
        this.expenseService.addComment(data).pipe(
          map((response) =>
            response.success
              ? ExpenseActions.addCommentSuccess(response)
              : ExpenseActions.addCommentFailure(response)
          ),
          catchError((error) =>
            of(
              ExpenseActions.addCommentFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  adminDirectApprove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExpenseActions.adminDirectApprove),
      mergeMap(({ ids }) =>
        this.expenseService.adminDirectApprove(ids).pipe(
          map((response) =>
            response.success
              ? ExpenseActions.adminDirectApproveSuccess(response)
              : ExpenseActions.adminDirectApproveFailure(response)
          ),
          catchError((error) =>
            of(
              ExpenseActions.adminDirectApproveFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );
}
